/**
 * ReviewChatPromptPopover: the gear on the ask panel (TRA-1082).
 *
 * Opens the team's instructions for the ask-about-this-run chat, the same
 * `review_chat` prompt the Prompts settings page edits. Save appends a
 * revision and makes it live for the whole team; the next question uses it.
 * The trajectory, the flag reason and the cite-your-evidence rule are added
 * around these instructions server-side, so nothing typed here can remove
 * them.
 *
 * Also holds the "run on open" switch (TRA-1081): whether the standard
 * question is asked as soon as a queue item loads.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, RotateCcw, Save, Settings } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { settingsPath } from '@/components/settings/sections'
import { useTeam } from '@/contexts/TeamContext'
import { useAgentPrompts, useSavePromptVersion, type AgentPrompt } from '@/hooks/useAgentPrompts'
import {
  REVIEW_CHAT_PROMPT_KIND,
  editDraft,
  isDraftStale,
  versionLine,
  type InstructionsDraft,
} from '@/components/review/reviewChatPrompt'

/** Backend `detail`, falling back to a generic message. */
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

interface PromptFormProps {
  prompt: AgentPrompt
  readOnly: boolean
}

function PromptForm({ prompt, readOnly }: PromptFormProps): React.JSX.Element {
  // No draft: the box shows the live body and follows it when a save lands
  // (yours or a teammate's). A draft is kept until it is saved or dropped, so
  // a teammate's save mid-edit cannot replace unsaved typing; the box says a
  // newer version is live instead.
  const [draft, setDraft] = useState<InstructionsDraft | null>(null)
  const saveMutation = useSavePromptVersion()
  // A draft that has become the live body (the drafter's own save landed)
  // has done its job: drop it so the box follows live again and a later
  // teammate save is not reported as freezing unsaved text.
  if (draft !== null && draft.text === prompt.body) {
    setDraft(null)
  }

  const body = draft === null ? prompt.body : draft.text
  const isDirty = body !== prompt.body
  const isDefault = body === prompt.default_body
  // Only worth a notice while there is unsaved typing to lose.
  const isStale = isDirty && isDraftStale(draft, prompt.body)

  const edit = (text: string): void => {
    setDraft(editDraft(draft, text, prompt.body))
  }

  const handleSave = (): void => {
    saveMutation.mutate(
      { kind: prompt.kind, data: { body } },
      {
        onSuccess: () => {
          // The box is read only while the request runs, so `body` is still
          // what the box shows. Not null: the list refetches in the
          // background, and a null draft would show the old live body until
          // that lands. Based on the live body the save replaced, so the
          // lagging list is not mistaken for a teammate's newer version.
          setDraft({ text: body, base: prompt.body })
          toast.success('Saved. The next question uses these instructions')
        },
        onError: (error) => {
          toast.error(errorMessage(error, 'Could not save the instructions'))
        },
      },
    )
  }

  return (
    <div className="space-y-2">
      <label htmlFor="review-chat-instructions" className="sr-only">
        Instructions for the ask panel
      </label>
      <Textarea
        id="review-chat-instructions"
        value={body}
        readOnly={readOnly || saveMutation.isPending}
        rows={14}
        spellCheck={false}
        onChange={(e) => {
          edit(e.target.value)
        }}
        className="max-h-[50vh] min-h-[10rem] resize-y font-mono text-xs leading-relaxed"
      />
      {isStale && (
        <p role="status" className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>A newer version is live ({versionLine(prompt)}). Your unsaved text is kept.</span>
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground"
            onClick={() => {
              setDraft(null)
            }}
          >
            Load newest
          </button>
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{versionLine(prompt)}</p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={readOnly || saveMutation.isPending || isDefault}
            onClick={() => {
              edit(prompt.default_body)
            }}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset to default
          </Button>
          <Button
            size="sm"
            disabled={readOnly || saveMutation.isPending || !isDirty}
            onClick={handleSave}
          >
            <Save className="mr-1 h-3 w-3" />
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Shared by the whole team. History and rollback are on the{' '}
        <Link to={settingsPath('prompts')} className="underline underline-offset-2 hover:text-foreground">
          Prompts page
        </Link>
        .
      </p>
    </div>
  )
}

interface ReviewChatPromptPopoverProps {
  runOnOpen: boolean
  onRunOnOpenChange: (enabled: boolean) => void
}

/**
 * The gear button plus its popover. Mounted in the ask panel header next to
 * Clear. The prompt list is fetched when the popover first opens, not with
 * the panel: most reviewers never open the gear.
 */
export function ReviewChatPromptPopover({
  runOnOpen,
  onRunOnOpenChange,
}: ReviewChatPromptPopoverProps): React.JSX.Element {
  const [open, setOpen] = useState(false)
  const { isRestricted, impersonatingTeamId } = useTeam()
  // Restricted demo teams and impersonating staff both read only; the API
  // refuses their writes.
  const readOnly = isRestricted || impersonatingTeamId !== null

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Ask panel settings"
          aria-label="Ask panel settings"
          className="flex h-6 w-6 flex-none items-center justify-center border border-border text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <Settings aria-hidden="true" className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[440px] max-w-[calc(100vw-24px)] rounded-sm p-3 shadow-none"
        aria-label="Ask panel settings"
      >
        <div className="mb-3 flex items-start justify-between gap-3 border-b border-border pb-3">
          <div>
            <Label htmlFor="review-chat-run-on-open" className="text-sm font-medium">
              Run on open
            </Label>
            <p className="text-xs text-muted-foreground">
              Ask &ldquo;Review this run.&rdquo; a moment after a run loads. Rows
              that already have a verdict are skipped. One model call per run;
              turn off to skim.
            </p>
          </div>
          <Switch
            id="review-chat-run-on-open"
            checked={runOnOpen}
            onCheckedChange={onRunOnOpenChange}
            className="mt-0.5"
          />
        </div>
        <div className="mb-2">
          <h3 className="text-sm font-medium text-foreground">Instructions</h3>
          <p className="text-xs text-muted-foreground">
            What the model is told before it reads the trajectory. The flag
            reason and the rule to cite requests are always added.
          </p>
        </div>
        {open && <PromptBody readOnly={readOnly} />}
      </PopoverContent>
    </Popover>
  )
}

function PromptBody({ readOnly }: { readOnly: boolean }): React.JSX.Element {
  // A teammate's save lands in another tab's cache, not this one. Watching
  // keeps `prompt.body` current while the popover is open, which is what
  // the stale notice compares the draft against.
  const promptsQuery = useAgentPrompts({ watchLive: true })
  const prompt = promptsQuery.data?.find((p) => p.kind === REVIEW_CHAT_PROMPT_KIND)

  if (promptsQuery.isError || (promptsQuery.data !== undefined && prompt === undefined)) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Could not load the instructions.
      </p>
    )
  }
  if (prompt === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        Loading
      </p>
    )
  }
  return <PromptForm prompt={prompt} readOnly={readOnly} />
}
